import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { mockTeam } from '../mock/mockData';
import * as Icons from 'lucide-react';

const ICON_MAP = {
  github: Icons.Github,
  linkedin: Icons.Linkedin,
  twitter: Icons.Twitter,
  youtube: Icons.Youtube,
  instagram: Icons.Instagram,
  email: Icons.Mail,
  portfolio: Icons.Globe,
  itch_io: Icons.Gamepad2
};

import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Team = () => {
  const { t, language } = useLanguage();
  const [sectionRef, isVisible] = useScrollAnimation(0.1);

  const coreTeam = mockTeam.filter(member => member.isCore);
  const contributors = mockTeam.filter(member => !member.isCore);

  const TeamMember = ({ member }) => {
    const name = member.name;
    const role = language === 'cz' ? member.roleCz : member.role;
    const bio = language === 'cz' ? member.bioCz : member.bio;

    let socialLinks = [];

    if (member.links) {
      member.links.forEach(link => {
        const key = link.label.toLowerCase();     // normalizace
        const icon = ICON_MAP[key];               // pokus o ikonu

        if (icon) {
          socialLinks.push({
            icon: icon,
            url: link.url,
            label: link.label
          });
        } else {
          console.warn("Unknown social icon:", link.label);
        }
      });
    }


    return (
      <div className="group h-full">
        <div className="bg-gray-900/50 backdrop-blur-sm border border-red-900/20 
       rounded-xl p-6 hover:border-red-600/50 transition-all 
       hover:shadow-xl hover:shadow-red-900/20 h-full flex flex-col">

          {/* Avatar */}
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center text-white text-xl font-bold">
              {member.initials}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-white group-hover:text-red-400 transition-colors">
                {name}
              </h3>
              <p className="text-red-500 text-sm font-medium">{role}</p>
            </div>
          </div>

          {/* Bio */}
          <p className="text-gray-400 text-sm leading-relaxed mb-4 flex-grow">
            {bio}
          </p>

          {/* Contact */}
          <div className="flex flex-col space-y-2 text-gray-500 mt-auto">
            {/* Header */}
            <h3 className="text-xs uppercase tracking-wide text-gray-400">Contact</h3>

            {/* Social Links */}
            <div className="flex space-x-3">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  aria-label={social.label}
                  className="w-10 h-10 rounded-full bg-red-900/20 hover:bg-red-900/40 flex items-center justify-center text-red-500 transition-colors"
                >
                  <social.icon size={14} />
                </a>
              ))}
            </div>
          </div>

        </div>
      </div>
    );
  };

  return (
    <section id="team" ref={sectionRef} className="py-20 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-red-600 to-red-400 bg-clip-text text-transparent pb-4">
            {t('team.title')}
          </h2>
          <p className="text-gray-400 text-lg">{t('team.subtitle')}</p>
        </div>

        {/* Core Team */}
        <div className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {coreTeam.map((member, index) => (
              <div
                key={member.id}
                className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${300 + index * 100}ms` }}
              >
                <TeamMember member={member} />
              </div>
            ))}
          </div>
        </div>

        {/* Contributors */}
        <div>
          <div
            className={`my-16 w-full flex items-center justify-center gap-4 transition-all duration-700 delay-700
  ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <div className="h-[2px] w-1/3 bg-gradient-to-r from-red-900/40 to-red-600/60"></div>

            <div className="w-3 h-3 rounded-full bg-red-600/60 shadow-lg shadow-red-900/40"></div>

            <div className="h-[2px] w-1/3 bg-gradient-to-l from-red-900/40 to-red-600/60"></div>
          </div>

          <div className="flex flex-wrap justify-center gap-6">

            {contributors.map((member, index) => (
              <div
                key={member.id}
                className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${800 + index * 100}ms` }}
              >
                <div className="w-full md:w-[350px]">
                  <TeamMember member={member} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Team;